import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetch } from '@/lib/api/client';
import { usePinSession } from '@/lib/auth/pin/usePinSession';
import { Keypad } from './Keypad';

const PIN_LENGTH = 6;

interface PinChangeResponse {
  token: string;
  expires_at: string;
  must_change_pin: boolean;
}

/**
 * Forced PIN change. Triggered when the active PIN session has must_change=true
 * (first login after creation/admin reset) or when the user explicitly opens it
 * to rotate. The user must type their current PIN once and the new PIN twice.
 */
export function PinChange() {
  const navigate = useNavigate();
  const { session, login, logout } = usePinSession();
  const [phase, setPhase] = useState<'current' | 'new' | 'confirm'>('current');
  const [currentPin, setCurrentPin] = useState('');
  const [newPin, setNewPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  if (!session) {
    return (
      <div className="min-h-screen flex items-center justify-center text-muted-foreground">
        No active session — return to the kiosk.
      </div>
    );
  }

  const active = phase === 'current' ? currentPin : phase === 'new' ? newPin : confirmPin;
  const setActive =
    phase === 'current' ? setCurrentPin : phase === 'new' ? setNewPin : setConfirmPin;

  function pushDigit(d: string) {
    setError(null);
    if (active.length >= PIN_LENGTH) return;
    setActive(active + d);
    if (active.length + 1 === PIN_LENGTH) {
      // Auto-advance after 6 digits.
      setTimeout(() => advance(), 50);
    }
  }
  function backspace() {
    setError(null);
    setActive(active.slice(0, -1));
  }

  function advance() {
    if (phase === 'current') setPhase('new');
    else if (phase === 'new') setPhase('confirm');
    else void submit();
  }

  async function submit() {
    if (!session) return;
    setBusy(true);
    setError(null);
    if (newPin !== confirmPin) {
      setError('PINs do not match');
      setPhase('new');
      setNewPin('');
      setConfirmPin('');
      setBusy(false);
      return;
    }
    const principalSnapshot = session.principal;
    try {
      const resp = await apiFetch<PinChangeResponse>('/auth/pin-change', {
        method: 'POST',
        body: JSON.stringify({ currentPin, newPin }),
      });
      login({
        token: resp.token,
        expiresAt: resp.expires_at,
        mustChangePin: false,
        principal: principalSnapshot,
      });
      navigate('/dashboard', { replace: true });
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'pin change failed');
      setPhase('current');
      setCurrentPin('');
      setNewPin('');
      setConfirmPin('');
    } finally {
      setBusy(false);
    }
  }

  const heading =
    phase === 'current'
      ? 'Enter your current PIN'
      : phase === 'new'
        ? 'Choose a new 6-digit PIN'
        : 'Confirm new PIN';

  return (
    <div className="min-h-screen flex flex-col items-center justify-center gap-6 p-6">
      <h2 className="text-xl font-semibold">{heading}</h2>
      <div className="flex gap-2">
        {Array.from({ length: PIN_LENGTH }).map((_, i) => (
          <div
            key={i}
            className={`w-10 h-12 border-2 rounded-md flex items-center justify-center text-2xl ${
              i < active.length ? 'border-primary' : 'border-border'
            }`}
          >
            {i < active.length ? '•' : ''}
          </div>
        ))}
      </div>
      <Keypad onDigit={pushDigit} onBackspace={backspace} onSubmit={advance} disabled={busy} />
      {error ? <p className="text-sm text-destructive">{error}</p> : null}
      <button
        type="button"
        onClick={() => {
          logout();
          navigate('/lock');
        }}
        className="text-xs text-muted-foreground underline underline-offset-4"
      >
        Cancel and sign out
      </button>
    </div>
  );
}
